/* eslint-disable quote-props */
'use client';
/* eslint-disable @next/next/no-img-element */

import { ServiceCardSkeleton } from '@/helpers/skeleton/services';
import { imageSrc } from '@/lib/imageSrc';
import { useCategoryDataQuery } from '@/redux/api/categoryApi';
import Head from 'next/head';
import { useRouter } from 'next/navigation';

interface Service {
  name: string;
  slug: string;
  image: string;
  summary?: string;
  description?: string;
  metaTitle?: string;
  metaDescription?: string;
}

const Services = () => {
  const router = useRouter();
  const { data, isLoading, isError } = useCategoryDataQuery({});

  const services: Service[] = data?.body || [];

  if (isLoading) return <ServiceCardSkeleton />;
  if (isError) return null;

  // Generate meta description from services if available
  const defaultMetaDescription =
    services.length > 0
      ? `Explore our ${services.length} specialized services including ${services
          .slice(0, 3)
          .map((s) => s.name)
          .join(', ')} and more.`
      : 'Discover our comprehensive range of professional services';

  return (
    <>
      <Head>
        <title>Our Services | Rehab Solution BD</title>
        <meta name="description" content={defaultMetaDescription} />
        <meta property="og:title" content="Our Services | Your Company Name" />
        <meta property="og:description" content={defaultMetaDescription} />
        <meta property="og:type" content="website" />
        {services.length > 0 && (
          <meta property="og:image" content={imageSrc(services[0].image)} />
        )}
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'ItemList',
            itemListElement: services.map((service, index) => ({
              '@type': 'ListItem',
              position: index + 1,
              item: {
                '@type': 'Service',
                name: service.name,
                description: service.summary || service.description || '',
                url: `${window.location.origin}/how-we-treat/${service.slug}`,
                image: imageSrc(service.image),
              },
            })),
          })}
        </script>
      </Head>

      <div className="">
        <div className="grid grid-cols-1 gap-3 p-3 md:grid-cols-2">
          {services.map((service, index) => (
            <div
              onClick={() => router.push(`/how-we-treat/${service.slug}`)}
              key={index}
              className="group/card relative h-[500px] w-full cursor-pointer overflow-hidden transition-all duration-300 lg:h-[600px] xl:h-[650px] 2xl:h-[700px]"
              itemScope
              itemType="https://schema.org/Service"
            >
              {/* Image with SEO attributes */}
              <img
                src={imageSrc(service.image)}
                alt={service.name}
                className="h-full w-full object-cover transition-transform duration-500 group-hover/card:scale-105"
                itemProp="image"
                loading={index > 2 ? 'lazy' : 'eager'}
              />

              {/* Overlay with gradient */}
              <div
                className="absolute inset-0 bg-gradient-to-t from-primary/40 via-primary/20 to-transparent opacity-0 transition-opacity duration-300 group-hover/card:opacity-100"
                aria-hidden="true"
              />

              {/* Service name with structured data */}
              <div className="absolute inset-0 flex items-end p-6">
                <h3
                  className="w-full -translate-y-4 transform text-center text-2xl font-bold text-white opacity-0 transition-all duration-500 group-hover/card:translate-y-0 group-hover/card:opacity-100"
                  itemProp="name"
                >
                  {service.name}
                </h3>
              </div>

              {/* Hidden metadata for SEO */}
              <meta
                itemProp="description"
                content={service.summary || service.description || ''}
              />
              <link
                itemProp="url"
                href={`${window.location.origin}/how-we-treat/${service.slug}`}
              />
            </div>
          ))}
        </div>
      </div>
    </>
  );
};

export default Services;
